import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Table, Drawer, SearchBar, BadgePill, EmptyState } from '@/components/campus';
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  QrCode,
  MoreHorizontal,
  Eye,
  Loader2,
  Building2,
  TrendingUp,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Status colors
const statusConfig = {
  draft: { label: 'Draft', variant: 'default' },
  published: { label: 'Published', variant: 'primary' },
  live: { label: 'Live', variant: 'success' },
  ended: { label: 'Ended', variant: 'muted' },
  cancelled: { label: 'Cancelled', variant: 'error' },
};

// ==================== EVENT DETAIL DRAWER ====================
const EventDetailDrawer = ({ event, isOpen, onClose }) => {
  if (!event) return null;

  const status = statusConfig[event.status] || statusConfig.draft;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Event Details" width="lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
            <Calendar className="w-8 h-8 text-electric-ice-blue" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-cloud-white">{event.event_name}</h3>
            <p className="text-soft-silver mt-1">{event.organization_name}</p>
            <div className="flex items-center gap-2 mt-2">
              <BadgePill variant={status.variant}>{status.label}</BadgePill>
              {event.visibility === 'public' && (
                <BadgePill variant="success">Public</BadgePill>
              )}
            </div>
          </div>
        </div>

        {/* Date/Time/Location */}
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <Clock className="w-5 h-5 text-electric-ice-blue" />
            <div>
              <p className="text-sm text-muted-text">Date & Time</p>
              <p className="text-cloud-white font-mono">{formatDateTime(event.start_time)}</p>
            </div>
          </div>
          
          {event.location && (
            <div className="flex items-center gap-3 p-3 bg-obsidian/50 rounded-lg border border-white/[0.06]">
              <MapPin className="w-5 h-5 text-cool-mint" />
              <div>
                <p className="text-sm text-muted-text">Location</p>
                <p className="text-cloud-white">{event.location}</p>
              </div>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <Users className="w-6 h-6 text-cool-mint mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(event.guest_count || 0)}</p>
            <p className="text-xs text-muted-text">Guests</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <QrCode className="w-6 h-6 text-electric-ice-blue mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{formatNumber(event.checkin_count)}</p>
            <p className="text-xs text-muted-text">Check-ins</p>
          </div>
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06] text-center">
            <TrendingUp className="w-6 h-6 text-soft-amber mx-auto mb-2" />
            <p className="text-2xl font-bold text-cloud-white font-mono">{event.capacity || '∞'}</p>
            <p className="text-xs text-muted-text">Capacity</p>
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div className="p-4 bg-obsidian/50 rounded-lg border border-white/[0.06]">
            <p className="text-sm text-muted-text mb-2">Description</p>
            <p className="text-soft-silver">{event.description}</p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

// ==================== MAIN EVENTS PAGE ====================
export const PlatformEvents = () => {
  const { session } = useAuth();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/platform/events?page_size=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events
  const filteredEvents = events.filter(e =>
    e.event_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.organization_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Table columns
  const columns = [
    {
      key: 'event_name',
      label: 'Event',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-electric-ice-blue/10 flex items-center justify-center">
            <Calendar className="w-5 h-5 text-electric-ice-blue" />
          </div>
          <div>
            <p className="font-medium text-cloud-white">{row.event_name}</p>
            <p className="text-xs text-muted-text">{row.organization_name}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'start_time',
      label: 'Date',
      render: (value) => (
        <span className="text-soft-silver font-mono text-sm">{formatDateTime(value)}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (value) => {
        const config = statusConfig[value] || statusConfig.draft;
        return <BadgePill variant={config.variant}>{config.label}</BadgePill>;
      },
    },
    {
      key: 'checkin_count',
      label: 'Check-ins',
      align: 'center',
      render: (value) => (
        <span className="font-mono text-cool-mint">{formatNumber(value)}</span>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="w-4 h-4 text-muted-text" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-graphite border-white/[0.08]">
            <DropdownMenuItem 
              onClick={() => { setSelectedEvent(row); setIsDetailOpen(true); }}
              className="text-cloud-white hover:bg-white/[0.05]"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-events-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-cloud-white">Events</h1>
        <p className="text-soft-silver text-sm mt-1">{events.length} events across all organizations</p>
      </div>

      {/* Search */}
      <SearchBar
        placeholder="Search events..."
        value={searchQuery}
        onChange={setSearchQuery}
      />

      {/* Table */}
      <Table
        columns={columns}
        data={filteredEvents}
        emptyState={
          <div className="py-12">
            <EmptyState
              icon="events"
              title="No events yet"
              description="Events will appear here once organizations start creating them."
            />
          </div>
        }
      />

      {/* Detail Drawer */}
      <EventDetailDrawer
        event={selectedEvent}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
      />
    </div>
  );
};

export default PlatformEvents;
